/**
 * AcademicStanding.jsx — Phase 1F: Academic Standing, GPA/CGPA, Progression, Holds
 *
 * Role-aware views:
 *   Admin / Registrar  → CohortStandingAdmin (term close, cohort standing table,
 *                                              hold management, progression, correction queue)
 *   Faculty            → CorrectionInitiation (initiate FINAL_GRADE.CORRECT)
 *   Student            → StudentAcademicRecord (CGPA tile, standing history, appeal button)
 *
 * INVARIANT: No GPA/CGPA computation in React.
 * All values come from canonical backend (INV-1F-GPA-01).
 * POLICY_NOT_CONFIGURED and REPEAT_POLICY_REQUIRED are never shown verbatim to students.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  TrendingUp, Award, AlertTriangle, CheckCircle, XCircle,
  ShieldAlert, ShieldCheck, Lock, Unlock, RefreshCw, ChevronRight,
  ClipboardList, BarChart3, Users, BookOpen, ArrowLeft, Loader2,
  FileText, MessageSquare, Send
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function apiError(e, fallback = "An error occurred") {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  return String(detail);
}

// ─── Standing badge ────────────────────────────────────────────────────────────
function StandingBadge({ standing }) {
  const map = {
    GOOD_STANDING: { label: "Good Standing", color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
    PROBATION:     { label: "Probation",      color: "text-amber-400 bg-amber-400/10 border-amber-400/30" },
    SUSPENSION:    { label: "Suspension",     color: "text-red-400 bg-red-400/10 border-red-400/30" },
    DISMISSED:     { label: "Dismissed",      color: "text-red-600 bg-red-600/10 border-red-600/30" },
  };
  const s = map[standing] || { label: standing || "—", color: "text-muted-foreground bg-muted border-border" };
  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-medium ${s.color}`}>
      {s.label}
    </span>
  );
}

// ─── Computation status pill ───────────────────────────────────────────────────
function StatusPill({ status }) {
  if (!status || status === "COMPUTED") return null;
  const friendly = {
    POLICY_NOT_CONFIGURED:  "Policy not configured",
    REPEAT_POLICY_REQUIRED: "Repeat policy required",
    NO_GRADES:              "No grades yet",
  };
  return (
    <span className="text-xs text-amber-400 italic ml-2">
      ({friendly[status] || status})
    </span>
  );
}

// ─── CGPA Tile ─────────────────────────────────────────────────────────────────
function CgpaTile({ studentId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    axios.get(`${API}/academic/students/${studentId}/cgpa`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [studentId, token]);

  if (loading) return <div className="animate-pulse h-16 bg-white/5 rounded-xl" />;

  const cgpa = data?.cgpa;
  const status = data?.computation_status;
  const isComputed = status === "COMPUTED" && cgpa;

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5 flex flex-col gap-1" data-testid="cgpa-tile">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">Cumulative GPA</div>
      {isComputed ? (
        <>
          <div className="text-3xl font-bold text-white" data-testid="cgpa-value">{cgpa}</div>
          <div className="text-xs text-muted-foreground">
            {data.credits_earned} / {data.credits_attempted} credits earned
          </div>
          {data.current_standing && (
            <div className="mt-1">
              <StandingBadge standing={data.current_standing.standing} />
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-muted-foreground italic" data-testid="cgpa-not-computed">
          Not yet computed
          <StatusPill status={status} />
        </div>
      )}
    </div>
  );
}

// ─── Student view ──────────────────────────────────────────────────────────────
function StudentAcademicRecord({ user }) {
  const [standing, setStanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAppeal, setShowAppeal] = useState(false);
  const [appealForm, setAppealForm] = useState({ final_grade_id: "", appeal_reason: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/academic/students/${user.student_id || user.person_id}/academic-standing`, {
      headers: { Authorization: `Bearer ${user.token}` },
    }).then(r => setStanding(r.data)).catch(() => setStanding(null)).finally(() => setLoading(false));
  }, [user]);

  const submitAppeal = async () => {
    if (!appealForm.final_grade_id || !appealForm.appeal_reason) {
      toast.error("Grade ID and reason are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/academic/grade-actions/appeal`, appealForm, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      toast.success("Appeal submitted — pending review");
      setShowAppeal(false);
      setAppealForm({ final_grade_id: "", appeal_reason: "" });
    } catch (e) {
      toast.error(apiError(e, "Failed to submit appeal"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">My Academic Record</h2>
      </div>

      <CgpaTile studentId={user.student_id || user.person_id} token={user.token} />

      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <h3 className="text-sm font-medium text-white mb-4">Standing History</h3>
        {loading ? (
          <div className="animate-pulse space-y-2">
            {[1,2].map(i => <div key={i} className="h-8 bg-white/5 rounded" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {(standing?.standing_history || []).length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No standing records yet</p>
            ) : (
              (standing?.standing_history || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                  <span className="text-muted-foreground text-xs">
                    {s.academic_term_id?.slice(0, 8)}…
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-mono">{s.gpa ?? "—"}</span>
                    <StandingBadge standing={s.standing} />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Grade Appeal */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-white">Grade Appeal</h3>
          <button
            data-testid="open-appeal-btn"
            onClick={() => setShowAppeal(!showAppeal)}
            className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1"
          >
            <MessageSquare size={13} />
            {showAppeal ? "Cancel" : "Submit Appeal"}
          </button>
        </div>
        {showAppeal && (
          <div className="space-y-3">
            <input
              data-testid="appeal-grade-id"
              placeholder="Final Grade ID"
              value={appealForm.final_grade_id}
              onChange={e => setAppealForm(f => ({ ...f, final_grade_id: e.target.value }))}
              className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground"
            />
            <textarea
              data-testid="appeal-reason"
              placeholder="Reason for appeal"
              rows={3}
              value={appealForm.appeal_reason}
              onChange={e => setAppealForm(f => ({ ...f, appeal_reason: e.target.value }))}
              className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground"
            />
            <button
              data-testid="submit-appeal-btn"
              onClick={submitAppeal}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Submit Appeal
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin / Registrar view ────────────────────────────────────────────────────
function CohortStandingAdmin({ user }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [termCloseForm, setTermCloseForm] = useState({
    student_id: "", academic_term_id: "", program_enrollment_id: ""
  });
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("term-close");

  const seedPolicy = async () => {
    try {
      const r = await axios.post(`${API}/academic/seed-isb-standing-policy`, {}, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const seeded = r.data.seeded.filter(s => s.action === "seeded").length;
      const skipped = r.data.seeded.filter(s => s.action === "skipped").length;
      toast.success(`Standing policy: ${seeded} seeded, ${skipped} already existed`);
    } catch (e) {
      toast.error(apiError(e, "Failed to seed policy"));
    }
  };

  const runTermClose = async () => {
    if (!termCloseForm.student_id || !termCloseForm.academic_term_id || !termCloseForm.program_enrollment_id) {
      toast.error("All three fields required");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/academic/term-close`, termCloseForm, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setResult(r.data);
      toast.success(`Term close complete — ${r.data.standing || r.data.standing_computation_status}`);
    } catch (e) {
      toast.error(apiError(e, "Term close failed"));
    } finally {
      setProcessing(false);
    }
  };

  const lookupCgpa = async () => {
    if (!termCloseForm.student_id) { toast.error("Student ID required"); return; }
    try {
      const r = await axios.get(`${API}/academic/students/${termCloseForm.student_id}/cgpa`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setResult(r.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to fetch CGPA"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Academic Standing — Admin</h2>
        <button
          data-testid="seed-policy-btn"
          onClick={seedPolicy}
          className="flex items-center gap-2 text-xs px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded border border-white/10"
        >
          <RefreshCw size={12} /> Seed ISB Policy
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-0">
        {[
          { id: "term-close", label: "Term Close", icon: RefreshCw },
          { id: "correction", label: "Grade Correction", icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? "border-sky-400 text-sky-400"
                : "border-transparent text-muted-foreground hover:text-white"
            }`}
          >
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "term-close" && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-4">
          <h3 className="text-sm font-medium text-white">Term Close — Compute GPA & Standing</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: "student_id", placeholder: "Student ID (UUID)" },
              { key: "academic_term_id", placeholder: "Academic Term ID (UUID)" },
              { key: "program_enrollment_id", placeholder: "Program Enrollment ID (UUID)" },
            ].map(f => (
              <input
                key={f.key}
                data-testid={`term-close-${f.key}`}
                placeholder={f.placeholder}
                value={termCloseForm[f.key]}
                onChange={e => setTermCloseForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground"
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button
              data-testid="run-term-close-btn"
              onClick={runTermClose}
              disabled={processing}
              className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded disabled:opacity-50"
            >
              {processing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Run Term Close
            </button>
            <button
              data-testid="lookup-cgpa-btn"
              onClick={lookupCgpa}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm rounded border border-white/10"
            >
              <BarChart3 size={14} /> Lookup CGPA
            </button>
          </div>

          {result && (
            <div className="mt-4 p-4 rounded-lg bg-black/30 border border-white/10 text-sm space-y-2" data-testid="term-close-result">
              <div className="font-medium text-white mb-2">Result</div>
              {result.standing && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Standing:</span>
                  <StandingBadge standing={result.standing} />
                </div>
              )}
              {result.term_gpa && (
                <div><span className="text-muted-foreground">Term GPA:</span> <span className="text-white font-mono">{result.term_gpa}</span></div>
              )}
              {result.cgpa && (
                <div><span className="text-muted-foreground">CGPA:</span> <span className="text-white font-mono">{result.cgpa}</span></div>
              )}
              {result.entries_written !== undefined && (
                <div><span className="text-muted-foreground">Record entries written:</span> <span className="text-white">{result.entries_written}</span></div>
              )}
              {(result.gpa_computation_status || result.standing_computation_status || result.computation_status) && (
                <div className="text-amber-400 text-xs italic">
                  Status: {result.gpa_computation_status || result.standing_computation_status || result.computation_status}
                </div>
              )}
              {result.warning && (
                <div className="flex items-center gap-1 text-amber-400 text-xs">
                  <AlertTriangle size={12} /> Probation advisory — student may register but should meet with advisor
                </div>
              )}
              {result.hold_created && (
                <div className="flex items-center gap-1 text-red-400 text-xs">
                  <Lock size={12} /> Academic hold created: {result.hold_created}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "correction" && (
        <GradeCorrectionPanel user={user} />
      )}
    </div>
  );
}

// ─── Grade Correction Panel (Admin / Registrar / Faculty) ─────────────────────
function GradeCorrectionPanel({ user }) {
  const [form, setForm] = useState({
    final_grade_id: "", proposed_grade_letter: "", correction_reason: "",
    faculty_justification: ""
  });
  const [approvalForm, setApprovalForm] = useState({
    approval_id: "", decision: "APPROVED", decision_reason: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const initiateCorrection = async () => {
    if (!form.final_grade_id || !form.proposed_grade_letter || !form.correction_reason) {
      toast.error("Grade ID, proposed letter, and reason are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/academic/grade-actions/correct`, form, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setPendingAction(r.data);
      toast.success(`Correction requested — approval ID: ${r.data.approval_request_id?.slice(0,8)}…`);
    } catch (e) {
      toast.error(apiError(e, "Failed to initiate correction"));
    } finally {
      setSubmitting(false);
    }
  };

  const processApproval = async () => {
    if (!approvalForm.approval_id) { toast.error("Approval ID required"); return; }
    setApproving(true);
    try {
      const r = await axios.post(
        `${API}/academic/grade-actions/${approvalForm.approval_id}/correction-callback`,
        { decision: approvalForm.decision, decision_reason: approvalForm.decision_reason },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      toast.success(`Correction ${r.data.status} — recalculation complete`);
    } catch (e) {
      toast.error(apiError(e, "Approval failed"));
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-4">
        <h3 className="text-sm font-medium text-white">Initiate Grade Correction</h3>
        <p className="text-xs text-muted-foreground">
          Uses governed FINAL_GRADE.CORRECT Typed Action with approval workflow (Phase 1F).
          Requester cannot approve their own correction.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="correction-fg-id" placeholder="Final Grade ID (UUID)"
            value={form.final_grade_id}
            onChange={e => setForm(f => ({ ...f, final_grade_id: e.target.value }))}
            className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground" />
          <input data-testid="correction-grade-letter" placeholder="Proposed Grade Letter (e.g. B+)"
            value={form.proposed_grade_letter}
            onChange={e => setForm(f => ({ ...f, proposed_grade_letter: e.target.value }))}
            className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground" />
          <input data-testid="correction-reason" placeholder="Correction reason"
            value={form.correction_reason}
            onChange={e => setForm(f => ({ ...f, correction_reason: e.target.value }))}
            className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white col-span-2 placeholder:text-muted-foreground" />
        </div>
        <button data-testid="initiate-correction-btn" onClick={initiateCorrection} disabled={submitting}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm rounded disabled:opacity-50">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          Request Correction
        </button>
        {pendingAction && (
          <div className="text-xs text-emerald-400 mt-1" data-testid="correction-pending">
            Pending approval — ID: {pendingAction.approval_request_id}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-4">
        <h3 className="text-sm font-medium text-white">Process Correction Approval</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input data-testid="approval-id" placeholder="Approval Request ID"
            value={approvalForm.approval_id}
            onChange={e => setApprovalForm(f => ({ ...f, approval_id: e.target.value }))}
            className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground" />
          <select data-testid="approval-decision"
            value={approvalForm.decision}
            onChange={e => setApprovalForm(f => ({ ...f, decision: e.target.value }))}
            className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white">
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
          </select>
          <input data-testid="approval-reason" placeholder="Decision reason (optional)"
            value={approvalForm.decision_reason}
            onChange={e => setApprovalForm(f => ({ ...f, decision_reason: e.target.value }))}
            className="bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-muted-foreground" />
        </div>
        <button data-testid="process-approval-btn" onClick={processApproval} disabled={approving}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded disabled:opacity-50">
          {approving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          Process Decision
        </button>
      </div>
    </div>
  );
}

// ─── Entry point ───────────────────────────────────────────────────────────────
export default function AcademicStanding() {
  const { user } = useAuth();
  const role = user?.role || "";

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Not authenticated
      </div>
    );
  }

  if (role === "student") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <StudentAcademicRecord user={user} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <CohortStandingAdmin user={user} />
    </div>
  );
}
